import { faEdit, faUndoAlt } from '@fortawesome/free-solid-svg-icons'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Authorization from '../../Authentication/Authorization'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable, { CaptionButton } from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import Loading from '../../layout/Loading'
import PasswordPolicyService from '../../service/PasswordPolicyService'
import { formatBooleanColumn } from '../../utils/DatatableUtil'
import GatewayType from '../GatewayType'

const PasswordPolicy: GatewayType = () => {
  const [passwordPolicy, setPasswordPolicy] = useState({} as any)
  const [loading, setLoading] = useState(true)
  const { editDialogPropsSetter, confirmDialogPropsSetter } = useContext(DialogContext)
  const { t } = useTranslation()

  useEffect(() => {
    PasswordPolicyService.get()
      .then((passwordPolicy: any[]) => setPasswordPolicy(passwordPolicy))
      .finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: 'label',
        dataField: 'label',
        classes: 'font-weight-bolder',
        sort: true,
      },
      {
        text: 'value',
        dataField: 'value',
        sort: true,
        formatter: (cell: any, row: any) => (row.label === t('passwordPolicy.changeForced') ? formatBooleanColumn(cell) : cell),
      },
    ]
  }, [t])

  const data =
    Object.keys(passwordPolicy).length > 0
      ? [
          {
            label: t('passwordPolicy.minLength'),
            value: passwordPolicy.minLength,
          },
          {
            label: t('passwordPolicy.maxLength'),
            value: passwordPolicy.maxLength,
          },
          {
            label: t('passwordPolicy.minNumberCount'),
            value: passwordPolicy.minNumberCount,
          },
          {
            label: t('passwordPolicy.minCapitalCount'),
            value: passwordPolicy.minCapitalCount,
          },
          {
            label: t('passwordPolicy.minSpecialCount'),
            value: passwordPolicy.minSpecialCount,
          },
          {
            label: t('passwordPolicy.blacklist'),
            value: passwordPolicy.blacklist?.join(', '),
          },
          {
            label: t('passwordPolicy.changeForced'),
            value: passwordPolicy.changeForced,
          },
          {
            label: t('passwordPolicy.maxDays'),
            value: passwordPolicy.maxDays,
          },
        ]
      : []

  var captionButtons: CaptionButton[] = [
    {
      onClick: () => configurePasswordPolicyDialog(t, editDialogPropsSetter, setPasswordPolicy, passwordPolicy),
      text: t('edit'),
      icon: faEdit,
      permissions: Authorization.userManagement.passwordPolicy.edit,
    },
    {
      onClick: () => restoreDefaultDialog(t, confirmDialogPropsSetter, setPasswordPolicy),
      text: t('restoreDefaults'),
      icon: faUndoAlt,
      permissions: Authorization.userManagement.passwordPolicy.edit,
    },
  ]

  return (
    <Loading isLoading={loading}>
      <DataTable
        keyField='label'
        data={data}
        columns={columns}
        headerClasses='d-none'
        searchable={false}
        caption={t('passwordPolicy.text')}
        captionButtons={captionButtons}
      />
    </Loading>
  )
}

function configurePasswordPolicyDialog(t: TFunction, editDialogPropsSetter: any, setPasswordPolicy: React.Dispatch<any>, passwordPolicy: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('passwordPolicy.configureHeader'),
    onSubmit: PasswordPolicyService.update,
    onSubmitExtraArgs: [t, setPasswordPolicy],
    labelGridSize: 5,
    data: [
      {
        id: 'minLength',
        value: passwordPolicy.minLength,
        type: 'number',
        label: t('passwordPolicy.minLength'),
        placeholder: '12',
        required: true,
        requiredMessage: t('invalidMessage', { label: 'minLength' }),
      },
      {
        id: 'maxLength',
        value: passwordPolicy.maxLength,
        type: 'number',
        label: t('passwordPolicy.maxLength'),
        placeholder: '0',
      },
      {
        id: 'minNumberCount',
        value: passwordPolicy.minNumberCount,
        type: 'number',
        label: t('passwordPolicy.minNumberCount'),
        placeholder: '0',
      },
      {
        id: 'minCapitalCount',
        value: passwordPolicy.minCapitalCount,
        type: 'number',
        label: t('passwordPolicy.minCapitalCount'),
        placeholder: '0',
      },
      {
        id: 'minSpecialCount',
        value: passwordPolicy.minSpecialCount,
        type: 'number',
        label: t('passwordPolicy.minSpecialCount'),
        placeholder: '0',
      },
      {
        id: 'blacklist',
        value: passwordPolicy.blacklist?.join(', '),
        type: 'textarea',
        label: t('passwordPolicy.blacklist'),
        placeholder: '0',
      },
      {
        groupLabel: t('passwordPolicy.changeForced'),
        groupName: 'changeForced',
        type: 'switch',
        checkboxes: [
          {
            id: 'changeForced',
            value: passwordPolicy.changeForced,
          },
        ],
      },
      {
        id: 'maxDays',
        value: passwordPolicy.maxDays,
        type: 'number',
        label: t('passwordPolicy.maxDays'),
        placeholder: '0',
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

function restoreDefaultDialog(t: TFunction, confirmDialogPropsSetter: any, setPasswordPolicy: any) {
  const resetDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('passwordPolicy.resetHeader'),
    body: t('passwordPolicy.resetBody'),
    submitText: t('yes'),
    onSubmit: PasswordPolicyService.reset,
    onSubmitExtraArgs: [t, setPasswordPolicy],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(resetDialogProps)
}

export default PasswordPolicy
